import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/api';
import ClientForm from '../components/ClientForm';
import { usePermissions } from '../hooks/usePermissions';
import ExportModal from '../components/ExportModal';
import { Button, Badge } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import {
  IconUsers, IconCurrencyDollar, IconRefresh, IconAlertTriangle,
  IconFileImport, IconFileDownload, IconFileExport,
  IconPlus, IconEdit, IconChartBar, IconCalculator, IconTrash,
  IconPrinter, IconX, IconChevronLeft, IconChevronRight,
} from '@tabler/icons-react';

export default function Clients() {
  const {
    hasPermission,
    canAccessClient,
    canModifyResource,
    canDeleteResource,
    currentUser,
  } = usePermissions();

  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [importData, setImportData] = useState([]);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importError, setImportError] = useState('');

  const [showEtatClient, setShowEtatClient] = useState(false);
  const [clientSelectionne, setClientSelectionne] = useState(null);
  const [detailsCalcul, setDetailsCalcul] = useState(null);

  const [periode, setPeriode] = useState({
    dateDebut: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    dateFin: new Date().toISOString().split('T')[0],
  });

  const [filtres, setFiltres] = useState({
    recherche: '',
    typePaiement: '',
    facturationAuto: '',
    adresse: '',
    status: ''
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const clientExportColumns = [
    { key: 'companyName', label: 'Raison Sociale', accessor: (c) => c.companyName || '-' },
    { key: 'contactName', label: 'Nom Contact', accessor: (c) => c.contactName || '-' },
    { key: 'email', label: 'Email', accessor: (c) => c.email || '-' },
    { key: 'phone', label: 'Téléphone', accessor: (c) => c.phone || '-' },
    { key: 'taxId', label: 'Matricule Fiscal', accessor: (c) => c.taxId || '-' },
    { key: 'typePaiement', label: 'Type Paiement', accessor: (c) => c.typePaiement || 'mensuel' },
    { key: 'forfait', label: 'Forfait HT', accessor: (c) => (c.forfait || 0).toFixed(3) },
    { key: 'soldeActuel', label: 'Solde Actuel', accessor: (c) => (c.soldeActuel || 0).toFixed(3) },
    { key: 'status', label: 'Statut', accessor: (c) => c.status || 'actif' },
  ];

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  useEffect(() => { chargerClients(); }, []);

  useEffect(() => { setPage(1); }, [clients.length]);

  useEffect(() => {
    const handlePaymentUpdate = () => { chargerClients(); };
    window.addEventListener('paymentUpdated', handlePaymentUpdate);
    return () => { window.removeEventListener('paymentUpdated', handlePaymentUpdate); };
  }, []);

  const chargerClients = async () => {
    setLoading(true);
    try {
      let endpoint = '/clients';
      if (currentUser?.role === 'collaborator') {
        try {
          const response = await api.get('/clients/assigned');
          const clientsAvecProchaineFacturation = response.data.map((client) => ({
            ...client,
            soldeActuel: client.soldeActuel || 0,
            prochaineFacturation: client.prochaineFacturation,
          }));
          setClients(clientsAvecProchaineFacturation || []);
          setLoading(false);
          return;
        } catch (apiError) {
          console.log("Endpoint /clients/assigned non disponible, chargement de tous les clients");
        }
      }
      const response = await api.get(endpoint);
      const clientsAvecProchaineFacturation = response.data.map((client) => ({
        ...client,
        soldeActuel: client.soldeActuel || 0,
        prochaineFacturation: client.prochaineFacturation,
      }));
      setClients(clientsAvecProchaineFacturation || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const getClientKey = (client) => client?.id || client?._id;

  const getClientsAccessibles = () => {
    if (!currentUser) return [];
    if (currentUser.role === 'admin') return clients;
    if (currentUser.role === 'collaborator') return clients.filter((client) => canAccessClient(getClientKey(client)));
    if (currentUser.role === 'client') return clients;
    return [];
  };

  const filtrerClients = () => {
    const clientsAccessibles = getClientsAccessibles();
    return clientsAccessibles.filter((client) => {
      if (filtres.recherche) {
        const rechercheLower = filtres.recherche.toLowerCase();
        const matchEntreprise = client.companyName?.toLowerCase().includes(rechercheLower);
        const matchContact = client.contactName?.toLowerCase().includes(rechercheLower);
        if (!matchEntreprise && !matchContact) return false;
      }
      if (filtres.typePaiement && client.typePaiement !== filtres.typePaiement) return false;
      if (filtres.facturationAuto !== '') {
        const facturationAutoBool = filtres.facturationAuto === 'true';
        if (client.facturationAuto !== facturationAutoBool) return false;
      }
      if (filtres.status && client.status !== filtres.status) return false;
      if (filtres.adresse && client.address) {
        const adresseLower = filtres.adresse.toLowerCase();
        if (!client.address.toLowerCase().includes(adresseLower)) return false;
      }
      return true;
    });
  };

  const mouvementsEtatClient = useMemo(() => {
    if (!detailsCalcul) return [];
    const facturesMvt = (detailsCalcul.factures || []).map((facture) => ({
      type: 'facture',
      date: facture.date,
      label: `Facture ${facture.number || ''}`.trim(),
      debit: parseFloat(facture.totalTTC || facture.totalTTC === 0 ? facture.totalTTC : 0) || 0,
      credit: 0,
    }));
    const paiementsMvt = (detailsCalcul.paiements || []).map((paiement) => ({
      type: 'paiement',
      date: paiement.paymentDate,
      label: `Paiement ${paiement.receiptNumber || ''}`.trim(),
      debit: 0,
      credit: parseFloat(paiement.totalAmount || paiement.totalAmount === 0 ? paiement.totalAmount : 0) || 0,
    }));
    const avoirsMvt = (detailsCalcul.avoirs || []).map((avoir) => ({
      type: 'avoir',
      date: avoir.creditNoteDate,
      label: `Avoir ${avoir.creditNoteNumber || ''}`.trim(),
      debit: 0,
      credit: parseFloat(avoir.totalTTC || 0) || 0,
    }));
    return [...facturesMvt, ...paiementsMvt, ...avoirsMvt].sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [detailsCalcul]);

  const verifierCalculSolde = async (clientId) => {
    const key = clientId;
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'client' && !canAccessClient(key)) {
      toastError("Vous n'avez pas accès à ce client");
      return;
    }
    try {
      const response = await api.get(`/solde/verifier-client/${key}`);
      const verification = response.data.verification;
      alert(
        `VÉRIFICATION SOLDE CLIENT: ${verification.client}\n\n` +
        `DÉTAIL DU CALCUL:\n` +
        `  Solde Initial: ${verification.soldeInitial} DT\n` +
        `  Total Factures: ${verification.totalFactures} DT (${verification.factures} factures)\n` +
        `  Total Avoirs: ${verification.totalAvoirs} DT (${verification.avoirs} avoirs)\n` +
        `  Total Paiements: ${verification.totalPaiements} DT (${verification.paiements} paiements)\n` +
        `  Solde Calculé: ${verification.soldeCalcule} DT\n` +
        `  Solde Actuel: ${verification.soldeActuel} DT\n` +
        `  Différence: ${verification.difference} DT\n\n` +
        `Formule: ${verification.soldeInitial} + ${verification.totalFactures} - ${verification.totalAvoirs} - ${verification.totalPaiements} = ${verification.soldeCalcule} DT`
      );
    } catch (error) {
      console.error('Erreur vérification solde:', error);
      toastError('Erreur lors de la vérification du calcul');
    }
  };

  const calculerDetailsSolde = async (clientId, dateDebut, dateFin) => {
    try {
      const response = await api.get(`/clients/${clientId}/etat-detaille`, { params: { dateDebut, dateFin } });
      return response.data;
    } catch (error) {
      console.error('Erreur appel état détaillé:', error);
      const clientResponse = await api.get(`/clients/${clientId}`);
      const client = clientResponse.data;
      return {
        client,
        soldeInitial: client.soldeInitial || 0,
        factures: [],
        totalFactures: 0,
        paiements: [],
        totalPaiements: 0,
        totalFacturesCumule: 0,
        totalPaiementsCumule: 0,
        soldeFinal: client.soldeActuel || 0,
        periode: { dateDebut, dateFin },
      };
    }
  };

  const afficherEtatClient = async (client) => {
    const key = getClientKey(client);
    if (currentUser?.role !== 'admin' && currentUser?.role !== 'client' && !canAccessClient(key)) {
      toastError("Vous n'avez pas accès à ce client");
      return;
    }
    setClientSelectionne(client);
    setLoading(true);
    try {
      const details = await calculerDetailsSolde(key, periode.dateDebut, periode.dateFin);
      setDetailsCalcul(details);
      setShowEtatClient(true);
    } catch (error) {
      console.error('Erreur chargement état client:', error);
      toastError('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const imprimerEtatClient = () => {
    if (!detailsCalcul) return;
    const printWindow = window.open('', '_blank');
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"/><title>État détaillé - ${detailsCalcul.client.companyName}</title><style>body{font-family:Arial,sans-serif;margin:20px;line-height:1.6}.header{text-align:center;margin-bottom:30px;border-bottom:2px solid #333;padding-bottom:20px}.header h1{margin:0;font-size:24px}.header h2{margin:10px 0 0;font-size:18px;color:#555}table{width:100%;border-collapse:collapse;margin:10px 0;font-size:11px}th,td{border:1px solid #ddd;padding:8px}th{background:#f8f9fa;font-weight:bold;text-align:center}.text-right{text-align:right}.text-red{color:#dc3545}.text-green{color:#28a745}.bg-gray{background:#f9f9f9}.font-bold{font-weight:bold}.footer{margin-top:30px;text-align:center;color:#666;font-size:10px}</style></head><body><div class="header"><h1>État détaillé du client</h1><h2>${detailsCalcul.client.companyName}</h2></div><table><thead><tr><th>Date</th><th>Libellé</th><th>Débit</th><th>Crédit</th><th>Solde</th></tr></thead><tbody>${(() => { let running = detailsCalcul.soldeInitial || 0; return mouvementsEtatClient.map((mvt, i) => { running = running + mvt.debit - mvt.credit; return `<tr class="${i%2===0?'':'bg-gray'}"><td>${mvt.date ? new Date(mvt.date).toLocaleDateString('fr-FR') : ''}</td><td>${mvt.label || (mvt.type==='facture'?'Facture':'Paiement')}</td><td class="text-right ${mvt.debit?'text-red':''}">${mvt.debit?`${mvt.debit.toFixed(2)} DT`:''}</td><td class="text-right ${mvt.credit?'text-green':''}">${mvt.credit?`${mvt.credit.toFixed(2)} DT`:''}</td><td class="text-right font-bold">${running.toFixed(2)} DT</td></tr>`; }).join(''); })()}</tbody></table><div class="footer">Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</div></body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const handleFileImport = (e) => {
    if (!hasPermission('clients', 'create')) { toastError("Vous n'avez pas la permission d'importer des clients"); return; }
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const convertedData = convertExcelData(jsonData);
        setImportData(convertedData);
        setImportError('');
        setShowImportPreview(true);
        setShowModal(true);
      } catch (error) {
        console.error('Erreur lecture fichier Excel:', error);
        toastError('Erreur lors de la lecture du fichier Excel');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const convertExcelData = (excelRows) => {
    if (excelRows.length === 0) return [];
    const headers = excelRows[0].map((header) => header ? header.toString().trim() : '');
    const dataRows = excelRows.slice(1);
    const columnMapping = {
      companyName: ['Raison Sociale', 'Entreprise', 'Nom', 'companyName', 'Société', 'Client'],
      formeJuridique: ['Forme Juridique', 'formeJuridique', 'Type Société'],
      natureActivite: ['Activité', 'Nature Activité', 'natureActivite'],
      status: ['Statut', 'Status', 'Etat'],
      contactName: ['Nom Contact', 'Contact', 'Prenom', 'Prénom', 'contactName'],
      email: ['Email', 'email', 'Mail', 'Courriel'],
      phone: ['Téléphone', 'Telephone', 'phone', 'Tél', 'Tel', 'Phone'],
      address: ['Adresse', 'address', 'Address', 'Adresse Client'],
      taxId: ['Matricule Fiscal', 'Identifiant Fiscal', 'taxId', 'TaxId', 'MF'],
      typePaiement: ['Type Paiement', 'typePaiement', 'Type de Paiement'],
      forfait: ['Forfait HT', 'Forfait', 'forfait', 'Montant Forfait', 'Tarif'],
      soldeInitial: ['Solde Initial', 'soldeInitial', 'Solde de départ'],
      facturationAuto: ['Facturation Auto', 'facturationAuto', 'Facturation Automatique'],
      dateAnniversaire: ['Date Début', 'Date Anniversaire', 'dateAnniversaire', 'Date de début'],
    };
    const columnIndices = {};
    Object.keys(columnMapping).forEach((key) => {
      columnMapping[key].forEach((possibleHeader) => {
        const index = headers.findIndex((header) => header && header.toLowerCase() === possibleHeader.toLowerCase());
        if (index !== -1 && columnIndices[key] === undefined) columnIndices[key] = index;
      });
    });
    return dataRows.map((row) => {
      const clientData = {};
      Object.keys(columnIndices).forEach((key) => {
        const index = columnIndices[key];
        if (index !== undefined && row[index] !== undefined) {
          let value = row[index];
          if (typeof value === 'string') value = value.trim();
          clientData[key] = value;
        }
      });
      return clientData;
    }).filter((item) => item.companyName);
  };

  const confirmerImport = async () => {
    if (!hasPermission('clients', 'create')) { toastError("Vous n'avez pas la permission de créer des clients"); return; }
    setImportError('');
    const erreurs = [];
    let clientsImportes = 0;
    try {
      for (let i = 0; i < importData.length; i++) {
        const item = importData[i];
        const ligneNum = i + 2;
        try {
          let dateAnniversaire = '';
          if (item.dateAnniversaire) {
            const dateValue = item.dateAnniversaire;
            if (typeof dateValue === 'number') {
              const excelDate = new Date((dateValue - 25569) * 86400 * 1000);
              dateAnniversaire = excelDate.toISOString().split('T')[0];
            } else if (typeof dateValue === 'string') {
              const parsed = new Date(dateValue);
              if (!isNaN(parsed.getTime())) dateAnniversaire = parsed.toISOString().split('T')[0];
            }
          }
          const typePaiementRaw = item.typePaiement || 'mensuel';
          const typePaiement = typeof typePaiementRaw === 'string' ? typePaiementRaw.trim().toLowerCase() : 'mensuel';
          const typesValides = ['mensuel', 'trimestriel', 'semestriel', 'annuel'];
          if (!typesValides.includes(typePaiement)) {
            erreurs.push(`Ligne ${ligneNum}: Type de paiement "${typePaiementRaw}" invalide. Valeurs acceptées: ${typesValides.join(', ')}`);
            continue;
          }
          const facturationAutoRaw = item.facturationAuto;
          const facturationAuto = facturationAutoRaw && (facturationAutoRaw.toString().toLowerCase() === 'oui' || facturationAutoRaw.toString().toLowerCase() === 'true');
          const taxId = item.taxId ? item.taxId.toString().trim().toUpperCase() : '';
          if (taxId && !/^[0-9]{7}[A-Z]{3}[0-9]{3}$/.test(taxId)) {
            erreurs.push(`Ligne ${ligneNum}: Matricule fiscal "${taxId}" invalide. Format requis: 1234567ABC000`);
            continue;
          }
          const clientData = {
            companyName: (item.companyName || '').toString().trim(),
            contactName: (item.contactName || '').toString().trim(),
            email: (item.email || '').toString().trim(),
            phone: (item.phone || '').toString().trim(),
            address: (item.address || '').toString().trim(),
            taxId,
            typePaiement,
            forfait: parseFloat(item.forfait) || 0,
            soldeInitial: parseFloat(item.soldeInitial) || 0,
            dateAnniversaire,
            facturationAuto,
            soldeActuel: parseFloat(item.soldeInitial) || 0,
            formeJuridique: (item.formeJuridique || '').toString().trim(),
            natureActivite: (item.natureActivite || 'commercial').toString().trim(),
            status: (item.status || 'actif').toString().trim().toLowerCase(),
          };
          if (!clientData.companyName) { erreurs.push(`Ligne ${ligneNum}: La raison sociale est obligatoire`); continue; }
          if (!clientData.taxId) { erreurs.push(`Ligne ${ligneNum} (${clientData.companyName}): Le matricule fiscal est obligatoire`); continue; }
          if (!clientData.dateAnniversaire) { erreurs.push(`Ligne ${ligneNum} (${clientData.companyName}): La date de début est obligatoire`); continue; }
          if (clientData.forfait <= 0) { erreurs.push(`Ligne ${ligneNum} (${clientData.companyName}): Le forfait doit être supérieur à 0`); continue; }
          await api.post('/clients', clientData);
          clientsImportes++;
        } catch (apiError) {
          let errorMsg = `Ligne ${ligneNum} (${item.companyName || 'inconnu'}): `;
          if (apiError.response?.data) {
            const data = apiError.response.data;
            if (data.details && Array.isArray(data.details)) errorMsg += data.details.join(', ');
            else if (data.error) errorMsg += data.error;
            else if (data.message) errorMsg += data.message;
            else errorMsg += 'Erreur de validation';
          } else errorMsg += apiError.message || 'Erreur inconnue';
          erreurs.push(errorMsg);
        }
      }
      if (erreurs.length > 0) setImportError(erreurs.join('\n'));
      if (clientsImportes > 0) await chargerClients();
      if (clientsImportes === importData.length) {
        setShowModal(false); setShowImportPreview(false); setImportData([]); setImportError('');
        toastError(`${clientsImportes} client(s) importé(s) avec succès !`);
      } else if (clientsImportes > 0) {
        toastError(`Import partiel: ${clientsImportes}/${importData.length} client(s) importé(s). Voir les erreurs ci-dessous.`);
      }
    } catch (error) {
      console.error('Erreur import clients:', error);
      setImportError("Erreur inattendue lors de l'import");
    }
  };

  const exporterModeleImport = () => {
    if (!hasPermission('clients', 'view')) { toastError("Vous n'avez pas la permission de télécharger des modèles"); return; }
    try {
      const modele = [
        { 'Raison Sociale': 'Exemple SARL', 'Nom Contact': 'Ali Ben Salah', 'Email': 'contact@exemple.tn', 'Téléphone': '71234567', 'Adresse': '123 Avenue Habib Bourguiba, Tunis 1000', 'Matricule Fiscal': '1234567ABC000', 'Type Paiement': 'mensuel', 'Forfait HT': 500, 'Solde Initial': 0, 'Facturation Auto': 'oui', 'Date Début': '2024-01-15' },
        { 'Raison Sociale': 'Tech Solutions TN', 'Nom Contact': 'Fatma Trabelsi', 'Email': 'info@techsolutions.tn', 'Téléphone': '98765432', 'Adresse': '45 Rue de la Liberté, Sfax 3000', 'Matricule Fiscal': '9876543XYZ001', 'Type Paiement': 'trimestriel', 'Forfait HT': 1500, 'Solde Initial': 250, 'Facturation Auto': 'oui', 'Date Début': '2024-03-01' },
      ];
      const worksheet = XLSX.utils.json_to_sheet(modele);
      worksheet['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 40 }, { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 12 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
      XLSX.writeFile(workbook, 'modele-import-clients.xlsx');
    } catch (error) {
      console.error('Erreur export modèle:', error);
      toastError("Erreur lors de l'export du modèle");
    }
  };

  const editerClient = async (client) => {
    if (!canModifyResource(client, 'clients')) { toastError("Vous n'avez pas la permission de modifier ce client"); return; }
    const clientId = client?._id || client?.id;
    if (!clientId) { toastError('Erreur : client invalide (ID manquant)'); return; }
    try {
      const response = await api.get(`/clients/${clientId}`);
      setEditingClient(response.data);
      setShowModal(true);
      setShowImportPreview(false);
    } catch (error) {
      console.error('Erreur chargement client (fallback):', error);
      setEditingClient(client);
      setShowModal(true);
      setShowImportPreview(false);
    }
  };

  const supprimerClient = async (clientId) => {
    if (!hasPermission('clients', 'delete')) { toastError("Vous n'avez pas la permission de supprimer des clients"); return; }
    if (currentUser?.role === 'collaborator') {
      const client = clients.find((c) => getClientKey(c) === clientId);
      if (!client || !canAccessClient(clientId)) { toastError('Vous ne pouvez supprimer que vos clients assignés'); return; }
    }
    if (!await showConfirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      await chargerClients();
      toastError('Client supprimé avec succès !');
    } catch (error) {
      console.error('Erreur suppression client:', error);
      toastError('Erreur lors de la suppression du client');
    }
  };

  const resetForm = () => { setEditingClient(null); };
  const handleFiltreChange = (e) => { const { name, value } = e.target; setFiltres((prev) => ({ ...prev, [name]: value })); setPage(1); };
  const reinitialiserFiltres = () => { setFiltres({ recherche: '', typePaiement: '', facturationAuto: '', adresse: '', status: '' }); setPage(1); };

  const clientsAccessibles = getClientsAccessibles();
  const clientsFiltres = filtrerClients();
  const totalPages = Math.ceil(clientsFiltres.length / PAGE_SIZE) || 1;
  const paginatedClients = clientsFiltres.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const typePaiementBadgeVariant = (type) => {
    if (type === 'mensuel') return 'info';
    if (type === 'trimestriel') return 'warning';
    if (type === 'semestriel') return 'success';
    return 'gray';
  };

  const statusBadgeVariant = (status) => {
    if (status === 'actif') return 'success';
    if (status === 'en_sommeil') return 'warning';
    if (status === 'ferme') return 'gray';
    return 'danger';
  };

  return (
    <div className="flex-1 p-6">
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">Gestion des Clients</h1>
        <p className="text-gray-500 text-sm">
          Connecté en tant que <strong>{currentUser?.name}</strong> ({currentUser?.email}) — <strong>{currentUser?.role}</strong>
          {currentUser?.role === 'collaborator' && <span className="ml-2">· Clients assignés : {clientsAccessibles.length}</span>}
        </p>
      </div>

      {/* Barre d'actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Liste des Clients</h3>
          <p className="text-sm text-gray-500">{clientsFiltres.length} clients trouvés sur {clientsAccessibles.length} accessibles</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasPermission('clients', 'create') && (
            <label className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 cursor-pointer transition-colors">
              <IconFileImport size={15} strokeWidth={1.6} />
              Importer Excel
              <input type="file" accept=".xlsx,.xls" onChange={handleFileImport} className="hidden" />
            </label>
          )}
          {hasPermission('clients', 'view') && (
            <Button variant="secondary" size="sm" onClick={exporterModeleImport}>
              <IconFileDownload size={15} strokeWidth={1.6} />
              Modèle d&apos;import
            </Button>
          )}
          {hasPermission('clients', 'view') && (
            <Button variant="info" size="sm" onClick={() => setShowExportModal(true)}>
              <IconFileExport size={15} strokeWidth={1.6} />
              Exporter
            </Button>
          )}
          {hasPermission('clients', 'create') && (
            <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowModal(true); setShowImportPreview(false); }}>
              <IconPlus size={15} strokeWidth={1.6} />
              Nouveau Client
            </Button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h4 className="font-medium text-gray-800 text-sm">Filtres de recherche</h4>
          {currentUser?.role === 'collaborator' && (
            <p className="text-xs text-blue-600 mt-1">Les filtres s&apos;appliquent uniquement à vos {clientsAccessibles.length} clients assignés</p>
          )}
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Recherche Entreprise/Contact', name: 'recherche', type: 'input', placeholder: 'Nom entreprise ou contact...' },
              { label: 'Adresse contient', name: 'adresse', type: 'input', placeholder: 'Rechercher par adresse...' },
            ].map(({ label, name, placeholder }) => (
              <div key={name}>
                <label className="block text-xs font-medium mb-1.5 text-gray-600">{label}</label>
                <input type="text" name={name} value={filtres[name]} onChange={handleFiltreChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder={placeholder} />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600">Statut Client</label>
              <select name="status" value={filtres.status || ''} onChange={handleFiltreChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="">Tous les statuts</option>
                <option value="actif">Actif</option>
                <option value="en_sommeil">En sommeil</option>
                <option value="ferme">Fermé</option>
                <option value="quitte_cabinet">Quitté le cabinet</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600">Type de Paiement</label>
              <select name="typePaiement" value={filtres.typePaiement} onChange={handleFiltreChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="">Tous les types</option>
                <option value="mensuel">Mensuel</option>
                <option value="trimestriel">Trimestriel</option>
                <option value="semestriel">Semestriel</option>
                <option value="annuel">Annuel</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600">Facturation Auto</label>
              <select name="facturationAuto" value={filtres.facturationAuto} onChange={handleFiltreChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                <option value="">Tous</option>
                <option value="true">Activée</option>
                <option value="false">Désactivée</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" size="sm" onClick={reinitialiserFiltres}>Réinitialiser les filtres</Button>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Clients accessibles', value: clientsAccessibles.length, icon: <IconUsers size={20} strokeWidth={1.5} className="text-blue-600" />, bg: 'bg-blue-50' },
          { label: 'Forfait moyen /mois', value: `${clientsAccessibles.length > 0 ? Math.round(clientsAccessibles.reduce((sum, c) => { const f = c.forfait || 0; if (c.typePaiement === 'trimestriel') return sum + f/3; if (c.typePaiement === 'semestriel') return sum + f/6; if (c.typePaiement === 'annuel') return sum + f/12; return sum + f; }, 0) / clientsAccessibles.length) : 0} DT`, icon: <IconCurrencyDollar size={20} strokeWidth={1.5} className="text-green-600" />, bg: 'bg-green-50' },
          { label: 'Facturation auto', value: clientsAccessibles.filter((c) => c.facturationAuto).length, icon: <IconRefresh size={20} strokeWidth={1.5} className="text-purple-600" />, bg: 'bg-purple-50' },
          { label: 'Soldes négatifs', value: clientsAccessibles.filter((c) => c.soldeActuel < 0).length, icon: <IconAlertTriangle size={20} strokeWidth={1.5} className="text-orange-600" />, bg: 'bg-orange-50' },
        ].map(({ label, value, icon, bg }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`${bg} p-2.5 rounded-lg flex-shrink-0`}>{icon}</div>
            <div>
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-xl font-bold text-gray-800">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Chargement des clients...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Client', 'Contact', 'MF', 'Type', 'Forfait', 'Solde', 'Fact. Auto', 'Prochaine Fact.', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedClients.map((client) => {
                  const canModify = canModifyResource(client, 'clients');
                  const canDelete = canDeleteResource(client, 'clients');
                  const clientKey = getClientKey(client);
                  return (
                    <tr key={clientKey} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm truncate max-w-[180px]" title={client.companyName}>{client.companyName || 'Non renseigné'}</span>
                            <Badge variant={statusBadgeVariant(client.status)}>{client.status || 'actif'}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 truncate max-w-[150px]">{client.email || 'Aucun email'}</span>
                            {client.formeJuridique && (
                              <span className="text-[9px] text-indigo-500 font-semibold border border-indigo-200 px-1 rounded">{client.formeJuridique}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">{client.contactName || '—'}</div>
                        <div className="text-xs text-gray-500">{client.phone || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-700">{client.taxId || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={typePaiementBadgeVariant(client.typePaiement)}>
                          {client.typePaiement ? client.typePaiement.substring(0, 3) : 'N/A'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-gray-900">{(client.forfait || 0).toFixed(3)} DT</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-semibold ${client.soldeActuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(client.soldeActuel || 0).toFixed(3)} DT
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={client.facturationAuto ? 'success' : 'danger'}>
                          {client.facturationAuto ? 'Oui' : 'Non'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-700">
                          {client.prochaineFacturation ? new Date(client.prochaineFacturation).toLocaleDateString('fr-FR', { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {canModify && (
                            <button onClick={() => editerClient(client)} title="Modifier"
                              className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 border border-amber-200 transition-colors">
                              <IconEdit size={14} strokeWidth={1.6} />
                            </button>
                          )}
                          <button onClick={() => afficherEtatClient(client)} title="État détaillé"
                            className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200 transition-colors">
                            <IconChartBar size={14} strokeWidth={1.6} />
                          </button>
                          <button onClick={() => verifierCalculSolde(clientKey)} title="Vérifier le solde"
                            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 border border-blue-200 transition-colors">
                            <IconCalculator size={14} strokeWidth={1.6} />
                          </button>
                          {canDelete && (
                            <button onClick={() => supprimerClient(clientKey)} title="Supprimer"
                              className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-red-200 transition-colors">
                              <IconTrash size={14} strokeWidth={1.6} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && clientsFiltres.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {clientsFiltres.length} client(s) — Page {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <IconChevronLeft size={16} strokeWidth={1.6} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${
                      p === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-200 text-gray-600 hover:bg-white'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <IconChevronRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}

        {!loading && clientsFiltres.length === 0 && (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <IconUsers size={32} strokeWidth={1.3} className="text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-700 mb-2">Aucun client trouvé</h3>
            <p className="text-sm text-gray-500 mb-6">
              {Object.values(filtres).some((v) => v)
                ? "Aucun client ne correspond à vos critères."
                : "Vous n'avez aucun client pour le moment."}
            </p>
            <div className="flex gap-3 justify-center">
              {hasPermission('clients', 'create') && (
                <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowModal(true); }}>
                  <IconPlus size={15} strokeWidth={1.6} />
                  Créer un client
                </Button>
              )}
              {hasPermission('clients', 'create') && (
                <label className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 cursor-pointer transition-colors">
                  <IconFileImport size={15} strokeWidth={1.6} />
                  Importer Excel
                  <input type="file" accept=".xlsx,.xls" onChange={handleFileImport} className="hidden" />
                </label>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Info collaborateurs */}
      {currentUser?.role === 'collaborator' && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 text-sm mb-1">Information pour les collaborateurs</h3>
          <p className="text-sm text-blue-700">Vous pouvez voir et modifier uniquement les clients qui vous sont assignés.</p>
        </div>
      )}

      {/* Modal État détaillé */}
      {showEtatClient && detailsCalcul && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-base font-semibold text-gray-800">État détaillé — {detailsCalcul.client?.companyName}</h2>
              <button onClick={() => setShowEtatClient(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <IconX size={18} strokeWidth={1.6} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Infos client */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2 text-sm text-gray-700">Client</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-700">
                  <div>
                    <div className="font-semibold">{detailsCalcul.client.companyName}</div>
                    {detailsCalcul.client.contactName && <div>{detailsCalcul.client.contactName}</div>}
                    {(detailsCalcul.client.address || detailsCalcul.client.city) && (
                      <div className="text-gray-500">{detailsCalcul.client.address} {detailsCalcul.client.city}</div>
                    )}
                  </div>
                  <div className="space-y-1">
                    {detailsCalcul.client.phone && <div><span className="font-medium">Tél : </span>{detailsCalcul.client.phone}</div>}
                    {detailsCalcul.client.email && <div><span className="font-medium">Email : </span>{detailsCalcul.client.email}</div>}
                    {detailsCalcul.client.taxId && <div><span className="font-medium">MF : </span>{detailsCalcul.client.taxId}</div>}
                  </div>
                </div>
              </div>
              {/* Période */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium mb-2 text-sm text-gray-700">Période</h4>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium mb-1">Date de début</label>
                    <input type="date" value={periode.dateDebut} onChange={(e) => setPeriode({ ...periode, dateDebut: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Date de fin</label>
                    <input type="date" value={periode.dateFin} onChange={(e) => setPeriode({ ...periode, dateFin: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <Button variant="primary" size="sm" className="mt-3" onClick={() => clientSelectionne && afficherEtatClient(clientSelectionne)}>
                  <IconRefresh size={14} strokeWidth={1.6} />
                  Actualiser
                </Button>
              </div>
              {/* Tableau comptable */}
              <div>
                <h4 className="font-medium mb-2 text-sm text-gray-700">
                  État comptable ({mouvementsEtatClient.length} mouvement(s))
                </h4>
                <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {['Date', 'Libellé', 'Débit (Factures)', 'Crédit (Paiements)', 'Solde'].map((h) => (
                          <th key={h} className="px-2 py-2 border-b border-gray-200 text-left text-xs font-semibold text-gray-600">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-gray-50">
                        <td className="px-2 py-1.5 text-xs text-gray-400"></td>
                        <td className="px-2 py-1.5 text-xs font-medium text-gray-600">Solde initial</td>
                        <td className="px-2 py-1.5 text-right text-xs text-gray-400">—</td>
                        <td className="px-2 py-1.5 text-right text-xs text-gray-400">—</td>
                        <td className="px-2 py-1.5 text-right text-xs font-semibold">{(detailsCalcul.soldeInitial || 0).toFixed(2)} DT</td>
                      </tr>
                      {(() => {
                        let running = detailsCalcul.soldeInitial || 0;
                        return mouvementsEtatClient.map((mvt, index) => {
                          running = running + mvt.debit - mvt.credit;
                          return (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-2 py-1.5 text-xs">{mvt.date ? new Date(mvt.date).toLocaleDateString('fr-FR') : ''}</td>
                              <td className="px-2 py-1.5 text-xs">{mvt.label || (mvt.type === 'facture' ? 'Facture' : 'Paiement')}</td>
                              <td className="px-2 py-1.5 text-right text-xs text-red-600">{mvt.debit ? `${mvt.debit.toFixed(2)} DT` : ''}</td>
                              <td className="px-2 py-1.5 text-right text-xs text-green-600">{mvt.credit ? `${mvt.credit.toFixed(2)} DT` : ''}</td>
                              <td className="px-2 py-1.5 text-right text-xs font-semibold">{running.toFixed(2)} DT</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200 shrink-0">
              <Button variant="success" size="sm" onClick={imprimerEtatClient}>
                <IconPrinter size={15} strokeWidth={1.6} />
                Imprimer l&apos;état
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowEtatClient(false)}>Fermer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Client (création / édition) */}
      {showModal && !showImportPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-800">{editingClient ? 'Modifier le client' : 'Nouveau client'}</h2>
                <p className="text-xs text-gray-500 mt-0.5">Remplissez les informations ci-dessous</p>
              </div>
              <button onClick={() => { setShowModal(false); resetForm(); setShowImportPreview(false); setImportData([]); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <IconX size={18} strokeWidth={1.6} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ClientForm
                initialData={editingClient}
                onSuccess={() => { setShowModal(false); resetForm(); setShowImportPreview(false); setImportData([]); chargerClients(); }}
                onCancel={() => { setShowModal(false); resetForm(); setShowImportPreview(false); setImportData([]); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Aperçu Import */}
      {showModal && showImportPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Aperçu de l&apos;importation</h2>
                <p className="text-xs text-gray-500 mt-0.5">{importData.length} client(s) détecté(s) dans le fichier</p>
              </div>
              <button onClick={() => { setShowModal(false); setShowImportPreview(false); setImportData([]); setImportError(''); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <IconX size={18} strokeWidth={1.6} />
              </button>
            </div>
            {importError && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 shrink-0">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Erreurs détectées</h4>
                    <div className="text-sm text-red-700 whitespace-pre-line max-h-32 overflow-y-auto">{importError}</div>
                  </div>
                  <button onClick={() => setImportError('')} className="text-red-400 hover:text-red-600">
                    <IconX size={16} strokeWidth={1.6} />
                  </button>
                </div>
              </div>
            )}
            <div className="flex-1 overflow-auto px-6 py-4">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    {['#', 'Raison Sociale', 'Contact', 'Matricule Fiscal', 'Type Paiement', 'Forfait', 'Date Début'].map((h) => (
                      <th key={h} className="border p-2 text-left text-xs font-semibold text-gray-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importData.map((item, index) => {
                    const taxIdValue = item.taxId ? item.taxId.toString().trim().toUpperCase() : '';
                    const taxIdValid = taxIdValue && /^[0-9]{7}[A-Z]{3}[0-9]{3}$/.test(taxIdValue);
                    const typePaiementValue = item.typePaiement ? item.typePaiement.toString().trim().toLowerCase() : 'mensuel';
                    const typePaiementValid = ['mensuel', 'trimestriel', 'semestriel', 'annuel'].includes(typePaiementValue);
                    return (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="border p-2 text-xs text-gray-500">{index + 1}</td>
                        <td className="border p-2 text-sm font-medium">{item.companyName || '-'}</td>
                        <td className="border p-2 text-sm">{item.contactName || '-'}</td>
                        <td className={`border p-2 text-sm font-mono ${taxIdValid ? 'text-green-600' : 'text-red-600 bg-red-50'}`}>
                          {taxIdValue || '-'}
                          {!taxIdValid && taxIdValue && <span className="block text-xs">⚠️ Format invalide</span>}
                        </td>
                        <td className={`border p-2 text-sm ${typePaiementValid ? '' : 'text-red-600 bg-red-50'}`}>{item.typePaiement || 'mensuel'}</td>
                        <td className="border p-2 text-sm">{item.forfait || 0} DT</td>
                        <td className="border p-2 text-sm">{item.dateAnniversaire || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
              <p className="text-xs text-gray-500">Les cellules en rouge indiquent des erreurs de format</p>
              <div className="flex gap-3">
                <Button variant="secondary" size="sm" onClick={() => { setShowModal(false); setShowImportPreview(false); setImportData([]); setImportError(''); }}>Annuler</Button>
                <Button variant="primary" size="sm" onClick={confirmerImport}>
                  <IconFileImport size={15} strokeWidth={1.6} />
                  Importer {importData.length} client(s)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          show={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Exporter les clients"
          columns={clientExportColumns}
          data={clientsFiltres}
          filename="clients"
        />
      )}
    </div>
  );
}
